/* eslint-disable react/no-multi-comp */
import React, { useState } from 'react';
import EditSeason from './EditSeason';

import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

// Mock season data
const initialMockSeason = {
  id: 1,
  name: 'Winter 2024',
  start_date: '2024-01-01',
  end_date: '2024-04-30',
};

const EditSeasonWrapper = () => {
  const [seasonOnEdit, setSeasonOnEdit] = useState(initialMockSeason);

  const editSeasonOutput = EditSeason({
    season: {
      ...initialMockSeason,
      isEditing: true,
    },
    seasonOnEdit,
    setSeasonOnEdit,
    setSeasonsOnEdit: () => {},
    onUpdate: () => {},
  });

  return (
    <div className="flex flex-row space-x-4 p-4 items-center justify-center">
      <div>{editSeasonOutput.name}</div>
      <div>{editSeasonOutput.start_date}</div>
      <div>{editSeasonOutput.end_date}</div>
      <div>{editSeasonOutput.action}</div>
    </div>
  );
};

// Storybook metadata
export default {
  title: 'Components/Seasons/EditSeason',
  component: EditSeason,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      `\nThe \`EditSeason\` component provides an inline editing interface for seasons in the Seasons table.
        \n\n###_**Features:**_
        \n- Editable fields for \`name\`, \`start_date\`, and \`end_date\`.
        \n- \`Update\` and \`Cancel\` buttons for handling user actions.
        \n- Improves the experience of usage by allowing quick changes without leaving the table view.\n\n`,
      // When & how to use it?
      `\n\nUse the \`EditSeason\` component to allow users to modify the details of a season inline within the Seasons table.
        \n\n###_**Key Features**_
        \n- **Editable Fields**:
        \n  - Name: Text input field for the season name.
        \n  - Start Date: Date picker for selecting the start date.
        \n  - End Date: Date picker for selecting the end date.
        \n\n- **Buttons**:
        \n  - \`Update\`: Saves the changes made to the season.
        \n  - \`Cancel\`: Reverts changes and exits the editing mode.\n\n`,
      // Current Usage
      '\nThe `EditSeason` component is used in the Seasons table to enable inline editing of season details. It ensures that users can quickly update season information without navigating to a separate page.\n\n',
      // Related Components
      `\n- **[Table](http://localhost:6006/?path=/docs/components-table--docs)**: Used to render the inline editable season.
      \n- **[Button](http://localhost:6006/?path=/docs/components-button-button--docs)**: Used for rendering \`Update\` and \`Cancel\` buttons.
      \n- **[TextInput](http://localhost:6006/?path=/docs/components-inputs-textinput--docs)**: Provides the input field for editing the season name.\n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    season: {
      control: { type: 'object' },
      description: 'The season data to be edited.',
      table: {
        type: { summary: 'object' },
        required: true,
      },
    },
    seasonOnEdit: {
      control: { type: 'object' },
      description: 'The season currently being edited.',
      table: {
        type: { summary: 'object' },
        required: true,
      },
    },
    setSeasonOnEdit: {
      control: { type: 'function' },
      description: 'Callback function to update the current season being edited.',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
    setSeasonsOnEdit: {
      control: { type: 'function' },
      description: 'Callback function to update the list of editable seasons.',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
    onUpdate: {
      control: { type: 'function' },
      description: 'Callback function triggered when the user clicks the `Update` button.',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
  },
};

// Story for documentation purposes
export const EditSeasonStory = () => <EditSeasonWrapper />;
EditSeasonStory.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThe `EditSeason` component is used within the Seasons table when a user selects a season to edit. This component allows for inline editing of season details, providing a seamless user experience.\n\n',
  // Where is it currently used on the product?
  '\nThe `EditSeason` component is used on the Seasons page within the table to edit season details inline.\n\n',
);
